import {STATES} from './stackFilter';
import {createSelector} from 'reselect';
import {selectPreference} from 'state/preference/preferenceSelectors';
import {selectFilterableColumnsByCardId} from 'state/dataSource/dataSourceSelectors';
import memoize from 'lodash/memoize';
import variadicKeyResolver from 'utilities/variadicKeyResolver';
import {STATIC_PAGE_IDS, SHARED_PAGE_ID} from 'app/PageConstants';
import {AUTHORITIES} from 'constants/AuthorityConstants';
import {selectHasAuthority} from 'state/environment/environmentSelectors';

export const isPageFiltersEnabled = (state, id) =>
  !STATIC_PAGE_IDS.includes(id) || SHARED_PAGE_ID !== id;

const reselectPageFiltersById = createSelector(
  state => state.stack.stackFilter.filtersById,
  state => state.stack.stackFilter.filters,
  (filtersById, filters) =>
    memoize(pageId =>
      Array.from(filtersById[pageId] || []).map(filterId => filters[filterId])
    )
);

export const selectPageFiltersById = (state, id) =>
  reselectPageFiltersById(state)(id);

const stripFlags = ({persisted, applied, ...rest}) => rest;

const reselectAppliedPageFiltersById = createSelector(
  reselectPageFiltersById,
  pageFiltersById =>
    memoize(pageId =>
      pageFiltersById(pageId)
        .filter(f => f.applied)
        .map(stripFlags)
    )
);

export const selectAppliedPageFiltersById = (state, id) =>
  reselectAppliedPageFiltersById(state)(id);

const reselectDataSourcePageFilters = createSelector(
  reselectPageFiltersById,
  pageFiltersById =>
    memoize(
      (pageId, dataSourceId) =>
        pageFiltersById(pageId).filter(f => f.dataSourceId === dataSourceId),
      variadicKeyResolver
    )
);

export const selectDataSourcePageFilters = (state, pageId, dataSourceId) =>
  reselectDataSourcePageFilters(state)(pageId, dataSourceId);

export const reselectPageFiltersByCardId = createSelector(
  (state, {cardId}) => state.card.entities[cardId],
  (state, {cardId}) => selectFilterableColumnsByCardId(state, cardId),
  (state, {pageId}) => selectPageFiltersById(state, pageId),
  (card, columns, filters) => {
    const datasources = (card && card.datasources) || [];
    const hasSingleDataSource = datasources.length === 1;
    const dataSourceIds = new Set(datasources.map(ds => ds.dataSourceId));
    const columnNames = new Set(columns.map(c => c.name));
    return filters
      .filter(
        pageFilter =>
          pageFilter.dataSourceId
            ? dataSourceIds.has(pageFilter.dataSourceId)
            : columnNames.has(pageFilter.column)
      )
      .map(filter => ({
        ...filter,
        dataSourceId: hasSingleDataSource
          ? datasources[0].dataSourceId
          : filter.dataSourceId,
      }));
  }
);

export const selectPageFiltersByCardId = (state, pageId, cardId) =>
  reselectPageFiltersByCardId(state, {pageId, cardId});

export const selectAppliedPageFiltersByCardId = createSelector(
  (state, pageId, cardId) =>
    reselectPageFiltersByCardId(state, {pageId, cardId}),
  filters => filters.filter(f => f.applied)
);

export const isLoadingPageFilters = (state, id) =>
  state.stack.stackFilter.filtersStateById[id] === STATES.LOADING;

export const hasLoadedPageFilters = (state, id) =>
  state.stack.stackFilter.filtersStateById[id] === STATES.LOADED;

export const selectHasQuerystringError = (state, id) =>
  !!state.stack.stackFilter.hasQueryStringErrorById[id];

const reselectDataSourceIds = createSelector(
  state => state.stack.stackFilter.dataSourcesById,
  dataSourcesById =>
    memoize(pageId => Array.from(dataSourcesById[pageId] || []))
);

export const selectDataSourceIds = (state, id) =>
  reselectDataSourceIds(state)(id);

export const selectControlsActive = (state, stackId) =>
  !!selectPreference(state, `page-controls-active-${stackId}`);

export const selectUserCanPersistPageFilters = state =>
  selectHasAuthority(state, AUTHORITIES.CONTENT_PAGE_EDIT) ||
  selectHasAuthority(state, AUTHORITIES.CONTENT_ADMIN);
